import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Mail, Phone, Send, Clock, MessageCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Eyebrow component
function Eyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

// Glass card component
function GlassCard({ children, className = "" }) {
  return (
    <div className={`glass-card p-6 md:p-8 ${className}`}>
      {children}
    </div>
  );
}

const offices = [
  {
    city: "Dubai, UAE — Head Office",
    address: "Office 120 & 121, Al Salam Building, Al Buteen, Murshid Bazar, Al Sabkha, Deira, Dubai, UAE — PO Box 1540",
    phone: "+971-45723353",
    phone2: "+971 55 695 3759 (WhatsApp)",
    email: "sales@optrenex.com",
  },
  {
    city: "Mumbai, India — Registered Office",
    address: "201, Om Palace CHS Ltd, Dr. Ambedkar Road, Khar Delivery, Mumbai 400052",
    phone: "+91 90933 65555",
    phone2: "+91 92555 55545",
    email: "business@optrenex.com",
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", subject: "", message: "", inquiry_type: "general"
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: "" });
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });
  const formRef = useRef(null);
  const formInView = useInView(formRef, { once: true, amount: 0.1 });

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ loading: false, success: true, error: "" });
      setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "", inquiry_type: "general" });
    } catch {
      setStatus({ loading: false, success: false, error: "Something went wrong. Please try again." });
    }
  };

  return (
    <main data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative" ref={heroRef}>
        <div className="absolute inset-0 grid-bg opacity-20" />
        <motion.div
          className="max-w-7xl mx-auto px-fluid text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Eyebrow>Get In Touch</Eyebrow>
          <h1 className="font-heading text-hero font-extrabold uppercase tracking-wider mt-4">
            Contact <span className="text-cyan">Us</span>
          </h1>
          <p className="mt-6 text-dim text-lg max-w-2xl mx-auto">
            Ready to partner with us? Have questions about our services? We're here to help.
          </p>
        </motion.div>
      </section>

      {/* Form + Offices */}
      <section className="pb-24 md:pb-32" ref={formRef}>
        <div className="max-w-7xl mx-auto px-fluid">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Contact Form */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 30 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <GlassCard>
                <h2 className="font-heading text-2xl font-bold uppercase tracking-wider text-ink mb-6">
                  Send Us a Message
                </h2>

                {status.success ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="contact-success"
                  >
                    <div className="w-16 h-16 rounded-full bg-cyan-soft flex items-center justify-center mx-auto mb-4">
                      <Send size={28} className="text-cyan" />
                    </div>
                    <h3 className="font-ui text-xl font-semibold text-ink mb-2">Message Sent!</h3>
                    <p className="text-dim text-sm">Our team will get back to you within 24 hours.</p>
                    <button
                      data-testid="send-another-btn"
                      onClick={() => setStatus({ loading: false, success: false, error: "" })}
                      className="mt-6 text-cyan font-mono text-sm underline underline-offset-4 hover:text-ink transition-colors"
                    >
                      Send Another Message
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim mb-1.5 block">Name *</label>
                        <input
                          data-testid="contact-name"
                          type="text" name="name" value={form.name} onChange={handleChange} required
                          className="w-full bg-white/5 border border-line rounded-lg px-4 py-3 text-ink text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30 transition-all placeholder:text-dim/50"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim mb-1.5 block">Email *</label>
                        <input
                          data-testid="contact-email"
                          type="email" name="email" value={form.email} onChange={handleChange} required
                          className="w-full bg-white/5 border border-line rounded-lg px-4 py-3 text-ink text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30 transition-all placeholder:text-dim/50"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim mb-1.5 block">Company *</label>
                        <input
                          data-testid="contact-company"
                          type="text" name="company" value={form.company} onChange={handleChange} required
                          className="w-full bg-white/5 border border-line rounded-lg px-4 py-3 text-ink text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30 transition-all placeholder:text-dim/50"
                          placeholder="Company name"
                        />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim mb-1.5 block">Phone</label>
                        <input
                          data-testid="contact-phone"
                          type="tel" name="phone" value={form.phone} onChange={handleChange}
                          className="w-full bg-white/5 border border-line rounded-lg px-4 py-3 text-ink text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30 transition-all placeholder:text-dim/50"
                          placeholder="+1 234 567 8900"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim mb-1.5 block">Inquiry Type</label>
                      <select
                        data-testid="contact-type"
                        name="inquiry_type" value={form.inquiry_type} onChange={handleChange}
                        className="w-full bg-white/5 border border-line rounded-lg px-4 py-3 text-ink text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30 transition-all"
                      >
                        <option value="general" className="bg-panel">General Inquiry</option>
                        <option value="customer" className="bg-panel">Become a Customer</option>
                        <option value="supplier" className="bg-panel">Become a Supplier</option>
                        <option value="support" className="bg-panel">Support</option>
                      </select>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim mb-1.5 block">Subject *</label>
                      <input
                        data-testid="contact-subject"
                        type="text" name="subject" value={form.subject} onChange={handleChange} required
                        className="w-full bg-white/5 border border-line rounded-lg px-4 py-3 text-ink text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30 transition-all placeholder:text-dim/50"
                        placeholder="How can we help?"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim mb-1.5 block">Message *</label>
                      <textarea
                        data-testid="contact-message"
                        name="message" value={form.message} onChange={handleChange} required rows={5}
                        className="w-full bg-white/5 border border-line rounded-lg px-4 py-3 text-ink text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30 transition-all resize-none placeholder:text-dim/50"
                        placeholder="Tell us about your requirements..."
                      />
                    </div>
                    {status.error && (
                      <p className="text-red-400 text-sm" data-testid="contact-error">{status.error}</p>
                    )}
                    <motion.button
                      type="submit"
                      disabled={status.loading}
                      data-testid="contact-submit-btn"
                      className="btn-primary inline-flex items-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {status.loading ? "Sending..." : "Send Message"} <Send size={16} />
                    </motion.button>
                  </form>
                )}
              </GlassCard>
            </motion.div>

            {/* Office Info */}
            <motion.div
              className="lg:col-span-2 space-y-6"
              initial={{ opacity: 0, y: 30 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <GlassCard>
                <h3 className="font-heading text-xl font-bold uppercase tracking-wider text-ink mb-5">
                  Our Offices
                </h3>
                <div className="space-y-6">
                  {offices.map((office, i) => (
                    <div key={i} className="pb-5 border-b border-line last:border-0 last:pb-0" data-testid={`office-${i}`}>
                      <h4 className="font-mono text-[11px] font-semibold text-cyan uppercase tracking-[0.15em] mb-2">
                        {office.city}
                      </h4>
                      <p className="text-dim text-sm mb-2">{office.address}</p>
                      <div className="flex items-center gap-2 text-dim text-xs mb-1">
                        <Phone size={12} className="text-cyan" />
                        {office.phone}
                      </div>
                      {office.phone2 && (
                        <div className="flex items-center gap-2 text-dim text-xs mb-1">
                          <Phone size={12} className="text-cyan" />
                          {office.phone2}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-dim text-xs">
                        <Mail size={12} className="text-cyan" />
                        {office.email}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>

              <GlassCard>
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={20} className="text-cyan" />
                  <h3 className="font-mono text-[11px] font-semibold text-ink uppercase tracking-[0.15em]">Response Time</h3>
                </div>
                <p className="text-dim text-sm">
                  We typically respond within 24 hours during business days.
                  For urgent inquiries, reach us via WhatsApp.
                </p>
              </GlassCard>

              <a
                href="https://wa.me/971556953759?text=Hello%20Optrenex%2C%20I%27d%20like%20to%20discuss%20a%20partnership."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="glass-card p-6 flex items-center gap-4 hover:border-[#25D366]/50 transition-all cursor-pointer block"
              >
                <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                  <MessageCircle size={22} className="text-white" fill="white" />
                </div>
                <div>
                  <h3 className="font-mono text-[11px] font-semibold text-ink uppercase tracking-[0.15em]">WhatsApp</h3>
                  <p className="text-dim text-xs mt-0.5">Chat with us for quick responses</p>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
